import { useState } from 'react';
import { type IntegrationDefinition, type IntegrationConnection } from '@/types/integrations';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';

interface DisconnectConfirmationDialogProps {
  integration: IntegrationDefinition | null;
  connection: IntegrationConnection | null;
  open: boolean;
  onClose: () => void;
  onConfirm: (connectionId: string, keepData: boolean) => Promise<void>;
}

export function DisconnectConfirmationDialog({
  integration,
  connection,
  open,
  onClose,
  onConfirm,
}: DisconnectConfirmationDialogProps) {
  const [keepData, setKeepData] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  const handleConfirm = async () => {
    if (!connection) return;

    setDisconnecting(true);
    try {
      await onConfirm(connection.id, keepData);
      onClose();
    } catch (error) {
      console.error('Disconnect failed:', error);
    } finally {
      setDisconnecting(false);
    }
  };

  if (!integration || !connection) return null;

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="text-3xl">{integration.logo}</div>
            <AlertDialogTitle>Disconnect {integration.name}?</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            This will remove the connection to {integration.name}. You can reconnect at any time.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <Alert variant="destructive">
          <AlertDescription>
            Disconnecting will stop all data synchronization and may affect features that depend on
            this integration.
          </AlertDescription>
        </Alert>

        <div className="flex items-center space-x-2 py-2">
          <Checkbox
            id="keepData"
            checked={keepData}
            onCheckedChange={(checked) => setKeepData(checked as boolean)}
          />
          <Label htmlFor="keepData" className="font-normal cursor-pointer">
            Keep historical data from this integration
          </Label>
        </div>

        <AlertDialogFooter>
          <Button variant="outline" onClick={onClose} disabled={disconnecting}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirm} disabled={disconnecting}>
            {disconnecting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              'Disconnect'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
